import { FiTrendingUp, FiChevronRight, FiLayers, FiCheckCircle, FiCpu, FiZap } from "react-icons/fi";
import { FaSnowflake, FaBullseye } from "react-icons/fa";

const plDays = (n) => {
  if (n % 10 === 1 && n % 100 !== 11) return 'dzień';
  return 'dni';
};

const plFreezes = (n) => {
  if (n === 1) return 'zamrożenie';
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 12 && mod100 <= 14) return 'zamrożeń';
  if (mod10 >= 2 && mod10 <= 4) return 'zamrożenia';
  return 'zamrożeń';
};

const GoalProgressWidget = ({
    goalName = "Zdać solidnie",
    current = 0,
    total = 100,
    label = "Postęp zadań",
    type = "tasks",
    onClick,
    // Stats footer
    streak,
    badgeImage,
    badgeTextColor,
    solvedToday,
    dailyGoal,
    freezes,
}) => {
  const safeTotal = total > 0 ? total : 100;
  const percentage = Math.min(100, Math.max(0, (current / safeTotal) * 100));
  const isCompleted = current >= total && total > 0;
  const hasStats = streak !== undefined;
  const goalDone = (solvedToday ?? 0) >= (dailyGoal ?? 1);

  return (
    <div
      onClick={onClick}
      className={`group relative w-full rounded-xl lg:rounded-2xl shadow-[0_12px_38px_-6px_rgba(35,56,85,0.30)] cursor-pointer transform transition-all duration-300 hover:scale-[1.01] hover:shadow-[0_22px_48px_-8px_rgba(35,56,85,0.36)] p-[1px] bg-gradient-to-br ${isCompleted ? 'from-[#E09F2E] via-[#F8AB3B] to-[#E09F2E]' : 'from-[#E09F2E]/50 via-[#233855]/50 to-[#E09F2E]/50'}`}
    >
      <div className="relative w-full bg-white rounded-[calc(0.75rem-1px)] lg:rounded-[calc(1rem-1px)] overflow-hidden">
      {/* --- SEKCJA CELU --- */}
      <div className="p-4 lg:p-5 pt-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3">
            {badgeImage ? (
              <div className="relative flex-shrink-0">
                <img src={badgeImage} alt="seria" className="w-16 h-auto" />
                {(streak ?? 0) > 0 && (
                  <span
                    className="absolute top-1/2 left-1/2 -translate-x-[55%] -translate-y-[50%] font-bold text-[18px] font-apotek leading-none"
                    style={{ color: badgeTextColor, textShadow: "0px 1px 2px rgba(0,0,0,0.15)" }}
                  >
                    {streak}
                  </span>
                )}
              </div>
            ) : (
              <div className={`p-2.5 rounded-xl shadow-md text-white transition-colors duration-300 ${isCompleted ? 'bg-[#E09F2E]' : 'bg-[#233855]'}`}>
                {isCompleted ? (
                  <FiCpu className="text-xl text-white" />
                ) : (
                  type === 'ranks' ? <FiLayers className="text-xl text-[#F8AB3B]" /> : <FiTrendingUp className="text-xl text-[#F8AB3B]" />
                )}
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-0.5">
                {isCompleted ? "Cel maturalny Osiągnięty" : "Twój cel maturalny"}
              </p>
              <h3 className="text-xl font-bold font-apotek tracking-wide text-[#233855] leading-none">
                {goalName}
              </h3>
            </div>
          </div>

          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
            <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
          </div>
        </div>

        {/* Pasek Postępu */}
        <div className="relative mt-2">
          <div className="flex justify-between text-sm font-semibold mb-1.5 text-gray-500 tracking-wide">
            {isCompleted ? (
              <span className="text-[#E09F2E] font-bold flex items-center gap-1">
                <FiCheckCircle /> Twój cel maturalny osiągnięty!
              </span>
            ) : (
              <span>{label}</span>
            )}
            <span className="text-[#233855] font-bold">{current} / {total}</span>
          </div>

          <div className="w-full h-3.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
            <div
              className={`h-full rounded-full shadow-sm relative overflow-hidden transition-all duration-1000 ease-out ${
                type === 'ranks'
                  ? "bg-gradient-to-r from-[#FFD700] to-[#F8AB3B]"
                  : "bg-gradient-to-r from-[#FFD700] to-[#F8AB3B] shadow-[0_0_10px_rgba(248,171,59,0.3)]"
              }`}
              style={{ width: `${percentage}%` }}
            >
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/40 to-transparent opacity-50" />
            </div>
          </div>

          {isCompleted && (
            <p className="text-[10px] text-gray-400 mt-2 text-right font-medium animate-in fade-in slide-in-from-top-1">
              Utrzymuj serię i rób powtórki, żeby zawsze być w formie!
            </p>
          )}
        </div>
      </div>

      {/* --- STATS FOOTER --- */}
      {hasStats && (
        <div className="flex items-start border-t border-gray-100 mx-4 pb-2.5 pt-2">

          {/* Seria */}
          <div className="flex-1 flex flex-col items-center gap-1">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#233855]">
              <FiZap className="text-[#E09F2E]" size={20} />
            </div>
            <p className="text-xs font-semibold text-gray-500 leading-tight text-center">
              {streak ?? 0} {plDays(streak ?? 0)} serii
            </p>
          </div>

          <div className="w-px bg-gray-100 self-stretch" />

          {/* Cel dzienny */}
          <div className="flex-1 flex flex-col items-center gap-1">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors duration-300 ${goalDone ? 'bg-[#233855]' : 'bg-[#E09F2E] shadow-[0_0_12px_rgba(224,159,46,0.4)]'}`}>
              <FaBullseye className={goalDone ? 'text-[#E09F2E]' : 'text-white'} size={20} />
            </div>
            <p className={`text-xs font-semibold leading-tight text-center ${goalDone ? 'text-[#007D00]' : 'text-[#C47D00]'}`}>
              {solvedToday ?? 0}/{dailyGoal ?? 1} cel dzienny
            </p>
          </div>

          <div className="w-px bg-gray-100 self-stretch" />

          {/* Zamrożenia */}
          <div className="flex-1 flex flex-col items-center gap-1">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${(freezes ?? 0) > 0 ? 'bg-[#233855]' : 'bg-gray-100'}`}>
              <FaSnowflake className={(freezes ?? 0) > 0 ? 'text-[#E09F2E]' : 'text-gray-300'} size={20} />
            </div>
            <p className="text-xs font-semibold text-gray-500 leading-tight text-center">
              {freezes ?? 0} {plFreezes(freezes ?? 0)}
            </p>
          </div>

        </div>
      )}

      {/* Ozdobne tło */}
      <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-[0.03] ${isCompleted ? 'bg-[#E09F2E]' : 'bg-[#F8AB3B]'}`} />
      </div>
    </div>
  );
};

export default GoalProgressWidget;
